import { useEffect, useState } from 'react';
import { Button, DatePicker, Divider, Form, InputNumber, Modal, Select } from "antd";
import { StorageBoxIcon } from "@components/icons/Icons";
import {
    useCollectionAddIssueMutation,
    useCollectionAddIssueWishlistRemoveIssueMutation,
    useCollectionAddIssuesMutation,
    useCollectionAddIssuesWishlistRemoveIssueMutation,
    useGetCollectionLocationsQuery,
    useWishlistRemoveIssueMutation
} from "@mwServices/User";
import TextArea from "antd/es/input/TextArea";
import { showResponse } from "@services/Api";
import { Issue } from "@mwServices/Issue";
import { QueryParmsViewTypes } from '@lib/constants';
import getSearchView from "@hooks/useSearchView";

interface AddComicModalProps {
    isModalOpen: boolean;
    issueIds?: number[];
    onCancel: () => void;
    issue?: Issue;
    onIssueUpdate?: (issue) => void;
    onIssuesUpdate?: (response) => void;
}

const SubmitTypes = {
    ADD_COMIC: `AddComic`,
    ADD_COMIC_REMOVE_WISHLIST: `AddComicRemoveWishlist`
}

const AddComicModal = ({ isModalOpen, issueIds, onCancel, issue, onIssueUpdate, onIssuesUpdate }: AddComicModalProps) => {
    const { view } = getSearchView() ?? { view: null };

    const [postAddIssue, addIssueResponse] = useCollectionAddIssueMutation();
    const [postAddIssues, addIssuesResponse] = useCollectionAddIssuesMutation();
    const [postAddIssueRemoveWishlist, addIssueRemoveWishlistResponse] = useCollectionAddIssueWishlistRemoveIssueMutation();
    const [postAddIssuesRemoveWishlist, addIssuesRemoveWishlistResponse] = useCollectionAddIssuesWishlistRemoveIssueMutation();

    const locationsQuery = useGetCollectionLocationsQuery({});
    const [form] = Form.useForm();
    const [showMore, setShowMore] = useState(false);
    const [submitButton, setSubmitButton] = useState('');

    useEffect(() => {
        showResponse(addIssueResponse, 'Issue added to collection.', 'Sorry, there was a problem adding the issue to collection.');
        if (addIssueResponse.isSuccess) {
            if (issue && onIssueUpdate && addIssueResponse.data.success) {
                onIssueUpdate({ ...issue, total_in_collection: addIssueResponse.data.result.owned });
            }
            onCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIssueResponse]);

    useEffect(() => {
        showResponse(addIssuesResponse, null, 'Sorry, there was a problem adding the issue(s) to collection.');
        if (addIssuesResponse.isSuccess) {
            if (addIssuesResponse.data.result) {
                addIssuesResponse.data.result.forEach(issueResult => {
                    showResponse(
                        { data: issueResult, isLoading: false },
                        'Issue added to collection.',
                        'Sorry, there was a problem adding the issue to collection.'
                    );
                });
                if (issueIds && onIssuesUpdate) onIssuesUpdate({ issueIds: issueIds, result: addIssuesResponse.data.result });
            }
            onCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIssuesResponse]);

    useEffect(() => {
        showResponse(addIssueRemoveWishlistResponse, 'Issue added to collection.', 'Sorry, there was a problem adding the issue to collection.');
        if (addIssueRemoveWishlistResponse.isSuccess) {
            if (issue && onIssueUpdate) onIssueUpdate({ ...issue, total_in_collection: addIssueRemoveWishlistResponse.data.result.owned });
            onCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIssueRemoveWishlistResponse]);

    useEffect(() => {
        showResponse(addIssuesRemoveWishlistResponse, null, 'Sorry, there was a problem adding the issue(s) to collection.');
        if (addIssuesRemoveWishlistResponse.isSuccess) {
            if (addIssuesRemoveWishlistResponse.data.result) {
                addIssuesRemoveWishlistResponse.data.result.forEach(issueResult => {
                    showResponse(
                        { data: issueResult, isLoading: false },
                        'Issue added to collection.',
                        'Sorry, there was a problem adding the issue to collection.'
                    );
                });
                if (issueIds && onIssuesUpdate) onIssuesUpdate({ issueIds: issueIds, result: addIssuesResponse.data.result });
            }
            onCancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addIssuesRemoveWishlistResponse]);

    const handleFinish = async (values: any) => {
        let sanitisedData: any = { ...values };

        if (values.purchase_date) sanitisedData.purchase_date = values.purchase_date.format("MM-DD-YYYY").split('-')

        if (issueIds) {
            sanitisedData.issue_ids = issueIds;
            
            if (SubmitTypes.ADD_COMIC === submitButton) {
                await postAddIssues(sanitisedData);
            }

            if (SubmitTypes.ADD_COMIC_REMOVE_WISHLIST === submitButton) {
                await postAddIssuesRemoveWishlist(sanitisedData);
            }

        } else if (issue) {
            sanitisedData.issue_id = issue.id;

            if (SubmitTypes.ADD_COMIC === submitButton) {
                await postAddIssue(sanitisedData);
            }

            if (SubmitTypes.ADD_COMIC_REMOVE_WISHLIST === submitButton) {
                await postAddIssueRemoveWishlist(sanitisedData);
            }
        }
    };

    const handleSubmitClick = (buttonName) => {
        setSubmitButton(buttonName);
        form.submit(); // Trigger form submission
    };

    return (
        <Modal
            title={null}
            open={isModalOpen}
            onCancel={onCancel}
            centered={true}
            destroyOnClose={true}
            footer={null}
            className="add-comic-modal"
        >
            <Form
                layout="vertical"
                form={form}
                initialValues={{
                    graded_by: 'RAW',
                    grade: '',
                    quantity: 1,
                    location: '',
                    autographed: 0,
                    notes: '',
                }}
                onFinish={handleFinish}
            >
                <div className="text-center">
                    <StorageBoxIcon className="icon50" /><br />
                    <span>For more accurate value estimations, Tell us more about this comic.</span>
                </div>
                <Divider />
                <div>
                    <p>Raw or Slabbed?:</p>
                    <Form.Item label="Select Grading Company or Raw:" name="graded_by">
                        <Select>
                            <Select.Option value="RAW">RAW</Select.Option>
                            <Select.Option value="CGC">CGC</Select.Option>
                            <Select.Option value="CBCS">CBCS</Select.Option>
                            <Select.Option value="PSA">PSA</Select.Option>
                            <Select.Option value="PGX">PGX</Select.Option>
                            <Select.Option value="EGS">EGS</Select.Option>
                            <Select.Option value="BECKETT">BECKETT</Select.Option>
                            <Select.Option value="HALO">HALO</Select.Option>
                            <Select.Option value="OTHER">OTHER</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="Select a Grade:" name="grade">
                        <Select>
                            <Select.Option value="">No Rating</Select.Option>
                            <Select.Option value="10.0">10.0 - Gem Mint</Select.Option>
                            <Select.Option value="9.9">9.9 - Mint</Select.Option>
                            <Select.Option value="9.8">9.8 - Near Mint / Mint</Select.Option>
                            <Select.Option value="9.6">9.6 - Near Mint+</Select.Option>
                            <Select.Option value="9.4">9.4 - Near Mint</Select.Option>
                            <Select.Option value="9.2">9.2 - Near Mint-</Select.Option>
                            <Select.Option value="9.0">9.0 - Very Fine / Near Mint</Select.Option>
                            <Select.Option value="8.5">8.5 - Very Fine+</Select.Option>
                            <Select.Option value="8.0">8.0 - Very Fine</Select.Option>
                            <Select.Option value="7.5">7.5 - Very Fine-</Select.Option>
                            <Select.Option value="7.0">7.0 - Fine / Very Fine</Select.Option>
                            <Select.Option value="6.5">6.5 - Fine+</Select.Option>
                            <Select.Option value="6.0">6.0 - Fine</Select.Option>
                            <Select.Option value="5.5">5.5 - Fine-</Select.Option>
                            <Select.Option value="5.0">5.0 - Very Good / Fine</Select.Option>
                            <Select.Option value="4.5">4.5 - Very Good+</Select.Option>
                            <Select.Option value="4.0">4.0 - Very Good</Select.Option>
                            <Select.Option value="3.5">3.5 - Very Good-</Select.Option>
                            <Select.Option value="3.0">3.0 - Good / Very Good</Select.Option>
                            <Select.Option value="2.5">2.5 - Good+</Select.Option>
                            <Select.Option value="2.0">2.0 - Good</Select.Option>
                            <Select.Option value="1.8">1.8 - Good-</Select.Option>
                            <Select.Option value="1.5">1.5 - Fair/Good</Select.Option>
                            <Select.Option value="1.0">1.0 - Fair</Select.Option>
                            <Select.Option value="0.5">0.5 - Poor</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="Quantity:" name="quantity">
                        <Select>
                            <Select.Option value={1}>1</Select.Option>
                            <Select.Option value={2}>2</Select.Option>
                            <Select.Option value={3}>3</Select.Option>
                            <Select.Option value={4}>4</Select.Option>
                            <Select.Option value={5}>5</Select.Option>
                            <Select.Option value={6}>6</Select.Option>
                            <Select.Option value={7}>7</Select.Option>
                            <Select.Option value={8}>8</Select.Option>
                            <Select.Option value={9}>9</Select.Option>
                            <Select.Option value={10}>10</Select.Option>
                        </Select>
                    </Form.Item>
                </div>
                <div className="text-center">
                    <Button
                        type="link"
                        onClick={() => setShowMore(!showMore)} >
                        {showMore ? 'Less-' : 'More+'}
                    </Button>
                </div>
                {showMore && (
                    <div>
                        <Form.Item label="Purchase Date:" name="purchase_date">
                            <DatePicker
                                className="width-100"
                                format={(value) => `${value.format("YYYY/MM/DD")}`}
                            />
                        </Form.Item>

                        <Form.Item label="Purchase Price:" name="purchase_price">
                            <InputNumber min={1} max={9999999} className="width-100" />
                        </Form.Item>
                        <Form.Item label="Location:" name="location">
                            <Select>
                                <Select.Option value="">None</Select.Option>
                                {locationsQuery.data && locationsQuery.data.map((location, i) => (
                                    <Select.Option
                                        key={i}
                                        value={location.location}>
                                        {location.location}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Notes:" name="notes">
                            <TextArea rows={4} maxLength={1000} showCount />
                        </Form.Item>
                        <Form.Item label="Autographed?:" name="autographed">
                            <Select>
                                <Select.Option value={0}>NO</Select.Option>
                                <Select.Option value={1}>YES</Select.Option>
                            </Select>
                        </Form.Item>
                    </div>
                )}
                <Divider />
                <div className="text-center">
                    <Button
                        onClick={() => handleSubmitClick(SubmitTypes.ADD_COMIC)}
                        type="primary"
                        shape="round"
                        size="large"
                    >
                        ADD COMIC
                    </Button>
                    {view === QueryParmsViewTypes.WISHLIST
                        && <><br /> <Button
                            onClick={() => handleSubmitClick(SubmitTypes.ADD_COMIC_REMOVE_WISHLIST)}
                            type="link"
                            shape="round"
                            size="large"
                        >
                            ADD COMIC & REMOVE FROM WISHLIST
                        </Button></>
                    }
                    <br />
                    <Button type="link" onClick={onCancel} >CANCEL</Button>
                </div>
            </Form>
        </Modal>
    );
};

export default AddComicModal;